import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Linking, Modal, RefreshControl, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import HtmlRenderer from 'react-native-render-html';

import { GET_MOBILE_PROCEDURE } from '../../graphql/procedure';
import { Text } from '../../components';
import { Card, EmptyState, Screen, Tag } from '../../design/components';
import { ImageViewer } from '../../components/image';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { getHtmlBoldStyle } from '../../utils/htmlBoldStyle';
import {
	PROCEDURE_PURPOSE_TEXT,
	getProcedureResponsibleName,
} from '../../../common/utils/people/procedureDocumentIntro';

const PROCEDURE_HTML_IGNORED_STYLES = [
	'background',
	'background-color',
	'color',
];

const normalizeImageSource = src => (
	src && src.startsWith('//') ? `https:${src}` : src
);

const ProcedureHtmlImage = ({
	alt,
	maxWidth,
	onPress,
	src,
}) => {
	const styles = useThemedStyles(createImageStyles);
	const imageUrl = useMemo(() => normalizeImageSource(src), [src]);
	const [imageSize, setImageSize] = useState(null);

	useEffect(() => {
		let isMounted = true;

		if (!imageUrl) {
			setImageSize(null);
			return () => {
				isMounted = false;
			};
		}

		Image.getSize(
			imageUrl,
			(imageWidth, imageHeight) => {
				if (!isMounted) {
					return;
				}

				const resolvedWidth = Math.min(maxWidth, imageWidth || maxWidth);
				const aspectRatio = imageWidth && imageHeight ? imageHeight / imageWidth : 0.5625;
				setImageSize({
					width: resolvedWidth,
					height: Math.max(1, Math.round(resolvedWidth * aspectRatio)),
				});
			},
			() => {
				if (!isMounted) {
					return;
				}

				setImageSize({
					width: maxWidth,
					height: Math.round(maxWidth * 0.5625),
				});
			},
		);

		return () => {
			isMounted = false;
		};
	}, [imageUrl, maxWidth]);

	if (!imageUrl) {
		return null;
	}

	const resolvedImageSize = imageSize || {
		width: maxWidth,
		height: Math.round(maxWidth * 0.5625),
	};

	return (
		<TouchableOpacity
			activeOpacity={0.85}
			accessibilityLabel={alt || 'Open procedure image'}
			accessibilityRole="imagebutton"
			onPress={() => onPress(imageUrl)}
			style={styles.container}
		>
			<Image
				source={{ uri: imageUrl }}
				style={[styles.image, resolvedImageSize]}
			/>
		</TouchableOpacity>
	);
};

const ProcedureDetailView = ({ route }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui } } = useTheme();
	const { width } = useWindowDimensions();
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [activeImageUrl, setActiveImageUrl] = useState('');
	const initialDocument = route?.params?.document || null;
	const procedureId = route?.params?.id || initialDocument?.id;
	const {
		data,
		error,
		loading,
		refetch,
	} = useQuery(GET_MOBILE_PROCEDURE, {
		variables: {
			id: procedureId,
		},
		skip: !procedureId,
		fetchPolicy: 'cache-and-network',
		notifyOnNetworkStatusChange: true,
	});
	const procedure = data?.getMobileProcedureDocument || initialDocument;
	const responsibleName = getProcedureResponsibleName(procedure);
	const publishedLabel = procedure?.publishedAt
		? moment(procedure.publishedAt).format('DD MMM YYYY')
		: '';
	const contentHtml = String(procedure?.contentHtml || '').trim();
	// Page gutters (2 x 18), card padding (2 x 16) and borders; the body is capped at the content width.
	const htmlContentWidth = Math.max(1, Math.min(width, ui.layout.maxContentWidth) - 70);
	const computeEmbeddedMaxWidth = useCallback(() => htmlContentWidth, [htmlContentWidth]);
	const closeImageViewer = useCallback(() => {
		setActiveImageUrl('');
	}, []);

	const refresh = useCallback(async () => {
		if (!refetch) {
			return;
		}

		setIsRefreshing(true);
		try {
			await refetch();
		}
		finally {
			setIsRefreshing(false);
		}
	}, [refetch]);

	const handleLinkPress = useCallback((_, href) => {
		if (href) {
			Linking.openURL(href);
		}
	}, []);
	const htmlRenderers = useMemo(() => ({
		img: (htmlAttribs, ...rendererArgs) => {
			const passProps = rendererArgs[2] || {};

			return (
				<ProcedureHtmlImage
					key={passProps.key}
					alt={htmlAttribs?.alt}
					maxWidth={htmlContentWidth}
					onPress={setActiveImageUrl}
					src={htmlAttribs?.src}
				/>
			);
		},
	}), [htmlContentWidth]);
	const activeImageUrls = useMemo(() => (
		activeImageUrl ? [{ url: activeImageUrl }] : []
	), [activeImageUrl]);

	const htmlTagStyles = useMemo(() => createHtmlTagStyles(ui), [ui]);

	const refreshControl = (
		<RefreshControl
			refreshing={isRefreshing}
			onRefresh={refresh}
			tintColor={ui.colors.accent}
		/>
	);

	const isInitialLoading = Boolean(loading && !procedure);

	if (!isInitialLoading && (error || !procedure)) {
		return (
			<Screen backLabel="Procedures" refreshControl={refreshControl}>
				<EmptyState
					tone="error"
					title="Procedure unavailable"
					message="Pull down to try loading it again."
					actionLabel="Try again"
					onAction={refresh}
				/>
			</Screen>
		);
	}

	return (
		<>
			<Screen
				backLabel="Procedures"
				isLoading={isInitialLoading}
				refreshControl={refreshControl}
			>
				{Boolean(procedure) && (
					<>
						<View style={styles.titleBlock}>
							<Text style={styles.kicker}>AVC standard operating procedure</Text>
							<Text style={styles.title} accessibilityRole="header">{procedure.title || 'Procedure'}</Text>
							<View style={styles.metaRow}>
								<Tag label={procedure?.version ? `Version ${procedure.version}` : 'Current version'} />
								{publishedLabel ? (
									<Tag label={`Published ${publishedLabel}`} />
								) : null}
							</View>
						</View>

						<Card style={styles.purposeCard}>
							<Text style={styles.purposeLabel}>Purpose</Text>
							<Text style={styles.purposeText}>{PROCEDURE_PURPOSE_TEXT}</Text>
							<View style={styles.responsibleBlock}>
								<Text style={styles.responsibleLabel}>Responsible person</Text>
								<Text style={styles.responsibleText}>{responsibleName || 'Not set'}</Text>
							</View>
						</Card>

						{contentHtml ? (
							<Card tone="raised">
								<HtmlRenderer
									baseFontStyle={styles.htmlBase}
									computeEmbeddedMaxWidth={computeEmbeddedMaxWidth}
									imagesMaxWidth={htmlContentWidth}
									enableExperimentalPercentWidth={true}
									html={contentHtml}
									ignoredStyles={PROCEDURE_HTML_IGNORED_STYLES}
									onLinkPress={handleLinkPress}
									renderers={htmlRenderers}
									tagsStyles={htmlTagStyles}
								/>
							</Card>
						) : (
							<EmptyState
								icon="file-text"
								title="No rich text content"
								message="This procedure does not have mobile-readable content yet."
							/>
						)}
					</>
				)}
			</Screen>
			<Modal
				visible={Boolean(activeImageUrl)}
				transparent={true}
				onRequestClose={closeImageViewer}
			>
				<ImageViewer
					close={closeImageViewer}
					imageUrls={activeImageUrls}
				/>
			</Modal>
		</>
	);
};

// Rich text from the People procedure editor, mapped onto the app type scale.
const createHtmlTagStyles = (ui) => {
	const { colors, type } = ui;
	const bold = getHtmlBoldStyle(ui);
	const bodyText = {
		...type.bodySmall,
		lineHeight: 23,
		color: colors.text,
	};

	return {
		p: {
			...bodyText,
			marginBottom: 12,
		},
		div: bodyText,
		// No font family on spans: <strong><span> must keep the bold family.
		span: {
			fontSize: bodyText.fontSize,
			lineHeight: bodyText.lineHeight,
			color: bodyText.color,
		},
		h1: {
			...type.hero,
			color: colors.text,
			marginTop: 18,
			marginBottom: 10,
		},
		h2: {
			...type.title,
			color: colors.text,
			marginTop: 17,
			marginBottom: 9,
		},
		h3: {
			...type.cardTitle,
			color: colors.text,
			marginTop: 15,
			marginBottom: 8,
		},
		h4: {
			...type.sectionTitle,
			color: colors.text,
			marginTop: 13,
			marginBottom: 7,
		},
		h5: {
			...type.rowTitleSemibold,
			color: colors.text,
			marginTop: 12,
			marginBottom: 6,
		},
		h6: {
			...type.secondarySemibold,
			color: colors.textSecondary,
			marginTop: 11,
			marginBottom: 5,
			textTransform: 'uppercase',
		},
		ul: {
			marginBottom: 12,
		},
		ol: {
			marginBottom: 12,
		},
		li: {
			...bodyText,
			marginBottom: 6,
		},
		strong: bold,
		b: bold,
		a: {
			color: colors.accent,
			textDecorationLine: 'underline',
		},
		table: {
			borderWidth: 1,
			borderColor: colors.borderStrong,
			marginVertical: 10,
		},
		th: {
			...bold,
			color: colors.text,
			backgroundColor: colors.card,
			padding: 6,
		},
		td: {
			color: colors.text,
			padding: 6,
		},
	};
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	titleBlock: {
		gap: space.sm,
	},
	kicker: {
		...type.overline,
		color: colors.accent,
	},
	title: {
		...type.title,
		color: colors.text,
	},
	metaRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		marginTop: space.xs,
	},
	purposeCard: {
		borderLeftWidth: 4,
		borderLeftColor: colors.accent,
		borderRadius: radius.card,
	},
	purposeLabel: {
		...type.sectionTitle,
		color: colors.text,
		marginBottom: 6,
	},
	purposeText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	responsibleBlock: {
		marginTop: space.md,
		paddingTop: 10,
		borderTopWidth: 1,
		borderTopColor: colors.border,
		gap: 2,
	},
	responsibleLabel: {
		...type.overline,
		color: colors.textSecondary,
	},
	responsibleText: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	htmlBase: {
		...type.bodySmall,
		lineHeight: 23,
		color: colors.text,
	},
});

const createImageStyles = ({ ui: { radius } }) => ({
	container: {
		alignSelf: 'center',
		marginVertical: 10,
		maxWidth: '100%',
	},
	image: {
		borderRadius: radius.tag,
		resizeMode: 'contain',
	},
});

export default ProcedureDetailView;
